import { FC, ReactNode, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Popover, Stack, Typography } from "@mui/material";
import { ComplexLinkBox, ComplexSideBarLinkUI } from "../../Layout.styles";
import { theme } from "../../../../theme";
import { SimpleSidebarLink } from "../SimpleSidebarLink";
import { ChevronDownIcon } from "../../../../assets/icons/Icons";
import { SidebarLinkTypes } from "../../LinkTypes";
import { useLocale } from "../../../../hooks/UseLocale";
import { Link, useNavigate } from "react-router-dom";

interface SidebarLinkItem {
    linkName?: SidebarLinkTypes;
    name?: string;
    path: string;
    id?: string | number;
}

interface BaseComplexSidebarLinkProps {
    activePathname: string;
    collapsed: boolean;
    showText: boolean;
    icon: ReactNode;
    label: string;
    links: SidebarLinkItem[];
    tooltip?: boolean;
    sxPopover?: object;
    mainPath?: string;
}

export interface ComplexLinkProps {
    activePathname: string;
    collapsed: boolean;
    showText: boolean;
}

export const BaseComplexSidebarLink: FC<BaseComplexSidebarLinkProps> = ({
    activePathname,
    collapsed,
    showText,
    icon,
    label,
    links,
    tooltip = true,
    sxPopover = {},
    mainPath
}) => {
    const [linkExpanded, setLinkExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const locale = useLocale();
    const navigate = useNavigate();

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        if (collapsed) {
            if (anchorEl) {
                setAnchorEl(null);
            } else {
                setAnchorEl(e.currentTarget);
            }
        }
    };

    const isLinkActive = (link: SidebarLinkItem) => activePathname === link.path;

    return (
        <ComplexSideBarLinkUI>
            <Box
                onClick={handleClick}
                sx={{ cursor: "pointer" }}
            >
                <Accordion
                    expanded={!collapsed && linkExpanded}
                    onChange={(_e, isExp) => !collapsed && setLinkExpanded(isExp)}
                >
                    <AccordionSummary expandIcon={!collapsed && <ChevronDownIcon />}>
                        <ComplexLinkBox isActive={isLinkActive(links[0])}>
                            {icon}
                            {showText && mainPath ? (
                                <Link
                                    to={`/${locale}/${mainPath}`}
                                    style={{ textDecoration: "none", color: "inherit" }}
                                >
                                    <Typography component="span">{label}</Typography>
                                </Link>
                            ) : (
                                showText && <Typography>{label}</Typography>
                            )}
                        </ComplexLinkBox>
                    </AccordionSummary>

                    {!collapsed && (
                        <AccordionDetails sx={{ paddingLeft: "24px!important" }}>
                            {links.map((link) => (
                                <SimpleSidebarLink
                                    key={link.path}
                                    linkName={link.linkName}
                                    name={link.name}
                                    path={link.path}
                                    isActive={isLinkActive(link)}
                                    sx={{
                                        cursor: "pointer",
                                        "&:hover": { color: theme.palette.primary.main },
                                    }}
                                    onClick={() => setAnchorEl(null)}
                                />
                            ))}
                        </AccordionDetails>
                    )}
                </Accordion>

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    onClose={() => setAnchorEl(null)}
                    disableEnforceFocus
                    disableRestoreFocus
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: "#000",
                                color: "#fff",
                                minWidth: 200,
                                borderRadius: 1,
                                p: 1,
                                ...sxPopover,
                            },
                        },
                    }}
                >
                    <Stack sx={{ p: 1 }}>
                        {links.map((link) => (
                            <SimpleSidebarLink
                                key={link.path}
                                linkName={link.linkName}
                                name={link.name}
                                path={link.path}
                                icon={icon}
                                activePathname={activePathname}
                                showText
                                sx={{
                                    cursor: "pointer",
                                    "&:hover": {
                                        color: theme.palette.primary.main,
                                        svg: { path: { fill: theme.palette.primary.main + "!important" } },
                                    },
                                }}
                                onClick={() => {
                                    setAnchorEl(null);
                                    navigate(`/${locale}/${link.path}`);
                                }}
                            />
                        ))}
                    </Stack>
                </Popover>
            </Box>
        </ComplexSideBarLinkUI>
    );
};
